import React from "react";
import { Box, Grid, Stack, Typography, Alert, Skeleton, Chip } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import BubbleChartOutlinedIcon from "@mui/icons-material/BubbleChartOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import { theme } from "../../../../Theme";
import { PROMO_COLORS } from "../../constants/colors";
import { CollapsibleSection } from "./CollapsibleSection";
import { fmtVol, fmtMoney, fmtX, fmtDiscountDepth } from "./helpers";

function KpiCard({ label, value, sub, accent, icon, trend }) {
  const color = accent || theme.palette.primary.main;
  return (
    <Box
      sx={{
        p: 2.25,
        borderRadius: 2,
        bgcolor: "#fff",
        border: "1px solid #E8ECF0",
        height: "100%",
        position: "relative",
        overflow: "hidden",
        transition: "box-shadow .2s ease, border-color .2s ease",
        "&:hover": {
          boxShadow: "0 4px 16px rgba(15,23,42,.07)",
          borderColor: `${color}40`,
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${color} 0%, ${color}66 100%)`,
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={1}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontWeight: 600,
            fontSize: 11,
            letterSpacing: "0.02em",
            pr: icon ? 1 : 0,
          }}
        >
          {label}
        </Typography>
        {icon && (
          <Box
            sx={{
              flexShrink: 0,
              width: 32,
              height: 32,
              borderRadius: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: `${color}12`,
              color,
            }}
          >
            {icon}
          </Box>
        )}
      </Stack>

      <Stack direction="row" alignItems="center" spacing={0.5} mt={1}>
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{
            color,
            letterSpacing: "-0.02em",
            fontSize: { xs: "1.2rem", sm: "1.35rem" },
          }}
        >
          {value}
        </Typography>
        {trend === "up" && (
          <TrendingUpIcon sx={{ color: PROMO_COLORS.safe, fontSize: 18 }} />
        )}
        {trend === "down" && (
          <TrendingDownIcon sx={{ color: PROMO_COLORS.threat, fontSize: 18 }} />
        )}
        {trend === "flat" && (
          <TrendingFlatIcon sx={{ color: "text.disabled", fontSize: 18 }} />
        )}
      </Stack>

      {sub && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.25, display: "block", fontSize: 11 }}
        >
          {sub}
        </Typography>
      )}
    </Box>
  );
}

export function HistoricalKPICards({ kpi, loading, error, filterBar }) {
  const empty = loading || !kpi;

  const cards = [
    {
      label: "Actual Volume",
      value: empty ? "—" : fmtVol(kpi.actualVolume),
      sub: "Total promotional volume (L)",
      icon: <BubbleChartOutlinedIcon sx={{ fontSize: 18 }} />,
      accent: theme.palette.primary.main,
    },
    {
      label: "Actual Contribution Margin",
      value: empty ? "—" : fmtMoney(kpi.actualCM),
      sub: kpi?.actualCMEstimated
        ? "Estimated using fallback cost for some SKUs"
        : "Total contribution margin",
      icon: <MonitorHeartOutlinedIcon sx={{ fontSize: 18 }} />,
      accent: PROMO_COLORS.safe,
      trend:
        !empty && kpi.actualCM != null
          ? kpi.actualCM >= 0
            ? "up"
            : "down"
          : null,
    },
    {
      label: "Actual ROI",
      value: empty ? "—" : fmtX(kpi.actualROI),
      sub: "Return on promotional spend",
      icon: <AssessmentOutlinedIcon sx={{ fontSize: 18 }} />,
      accent: theme.palette.primary.light,
      trend:
        !empty && kpi.actualROI != null
          ? kpi.actualROI >= 1.5
            ? "up"
            : kpi.actualROI >= 1.0
            ? "flat"
            : "down"
          : null,
    },
    {
      label: "Promo Spend",
      value: empty ? "—" : fmtMoney(kpi.promoSpend),
      sub: "Total promotional discount spend",
      icon: <LocalOfferOutlinedIcon sx={{ fontSize: 18 }} />,
      accent: PROMO_COLORS.warning,
    },
    {
      label: "Avg Discount Depth",
      value: empty ? "—" : fmtDiscountDepth(kpi.avgDiscountDepth),
      sub: "Average observed discount depth",
      icon: <PercentOutlinedIcon sx={{ fontSize: 18 }} />,
      accent: "#9C27B0",
    },
    {
      label: "Promo Weeks",
      value: empty ? "—" : kpi.promoWeeks != null ? fmtMoney(kpi.promoWeeks) : "—",
      sub: "Promo activity weeks in range",
      icon: <DateRangeOutlinedIcon sx={{ fontSize: 18 }} />,
      accent: "#0097A7",
    },
  ];

  return (
    <CollapsibleSection
      icon={<AssessmentOutlinedIcon sx={{ fontSize: 20 }} />}
      title="Historical KPI Summary"
      subtitle="Aggregated actuals from performance history API"
      filterBar={filterBar}
    >
      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2, fontSize: 13 }}>
          {error}
        </Alert>
      )}
      <Grid container spacing={2.25}>
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={6} sm={4} md={2} key={i}>
                <Skeleton variant="rounded" height={104} />
              </Grid>
            ))
          : cards.map((c) => (
              <Grid item xs={6} sm={4} md={2} key={c.label}>
                <KpiCard {...c} />
              </Grid>
            ))}
      </Grid>
    </CollapsibleSection>
  );
}
